import * as React from 'react';

import _ from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router';
import { faWrench } from '@fortawesome/sharp-regular-svg-icons';

import {
    useSociete,
    Fournisseur,
    useTiersService,
    setXIdSociete,
    TiersStateType,
} from '@europrocurement/l2d-domain';
import {
    ColumnDatatable,
    FiltersDatatableList,
    StoreDatatable,
} from '@europrocurement/flexy-datatable';
import { DataSource } from '@europrocurement/l2d-redux-utils';
import {
    ContextActionType,
    HeaderActionButton,
    ListActionButton,
    ListHeader,
    useDumbActionsContext,
} from '@europrocurement/flexy-components';
import { createPublisherIcon, FaOptionIcon } from '@europrocurement/l2d-icons';
import { PossibleTypes, useSearchBar } from '@europrocurement/l2d-modules';
import { useClickActions } from '@europrocurement/l2d-hooks';
import { Typography } from '@mui/material';
import { AppDispatch } from '../../../../redux/types';

import ListCellData from '../../components/ListCellData/ListCellData';

const PublishersListing: React.FunctionComponent = function () {
    const publishersDataSource: DataSource<Fournisseur> = useSelector(
        (s: TiersStateType) => s.tiers.fournisseur.main,
        _.isEqual,
    );

    const { getFournisseurs } = useTiersService();
    const { setIsSearchBarPath, setSearchBarType } = useSearchBar();
    const dispatch = useDispatch<AppDispatch>();
    const location = useLocation();
    const navigate = useNavigate();
    const xIdSociete = useSociete();
    const { navigateOnClick } = useClickActions({ navigate });

    const contextActions: ContextActionType[] = [
        {
            name: 'Créer un fournisseur',
            icon: <FaOptionIcon {...createPublisherIcon.props} />,
            action: () => undefined,
        },
    ];

    useDumbActionsContext(contextActions);

    // Activation du flag de recherche prescripteur
    React.useEffect(() => {
        setSearchBarType(PossibleTypes.Publisher);
        setIsSearchBarPath(true);
        return () => {
            setSearchBarType(undefined);
            setIsSearchBarPath(false);
        };
    }, [setIsSearchBarPath, setSearchBarType]);

    React.useEffect(() => {
        if (xIdSociete !== 12) {
            dispatch(setXIdSociete(12));
            dispatch(getFournisseurs({}));
        }
    }, [dispatch, getFournisseurs, xIdSociete]);

    const handleRowClick = React.useCallback(
        (__: React.MouseEvent<HTMLElement>, { id }: Fournisseur) => {
            navigateOnClick({ path: `${id}` });
        },
        [navigateOnClick],
    );

    const handleRowWheelClick = React.useCallback(
        (__: React.MouseEvent<HTMLElement>, { id }: Fournisseur) => {
            navigateOnClick({ path: `${location.pathname}/${id}`, openNewTab: true });
        },
        [location.pathname, navigateOnClick],
    );

    const columns: ColumnDatatable<Fournisseur>[] = React.useMemo(
        () => [
            {
                label: 'Siren',
                render: ({ siren, siret }) => (
                    <ListCellData
                        mainText={siren}
                        secondaryText={siret}
                    />
                ),
                isDisplayed: true,
                sortable: true,
                field: 'siren',
            },
            {
                label: 'Code Fournisseur Cegid',
                render: ({ codeFournisseurCegid }) => (
                    <Typography
                        variant="l2dtableCell"
                        color="text.gris70"
                    >
                        {codeFournisseurCegid}
                    </Typography>
                ),
                isDisplayed: true,
                sortable: false,
                field: 'codeFournisseurCegid',
            },
            {
                label: 'Libelle',
                render: ({ libelle }) => (
                    <Typography
                        variant="l2dtableCell"
                        color="text.gris70"
                    >
                        {libelle}
                    </Typography>
                ),
                isDisplayed: true,
                sortable: true,
                field: 'libelle',
            },
            {
                label: 'Type Remise',
                render: ({ typeRemise, rsfInclus }) => (
                    <ListCellData
                        mainText={`${typeRemise?.code}${rsfInclus ? ' (RSF Inclus)' : ''}`}
                        secondaryText={typeRemise?.libelle}
                    />
                ),
                isDisplayed: true,
                field: 'typeRemise',
            },
            {
                label: 'Actions',
                onClickCell: () => {},
                render: (publisher: Fournisseur) =>
                    publisher.id ? (
                        <ListActionButton
                            tooltip="Configurer"
                            onClick={() => navigate(`${publisher.id}`)}
                            icon={faWrench}
                        />
                    ) : null,
                isDisplayed: true,
            },
        ],
        [navigate],
    );

    const filters: FiltersDatatableList = [
        {
            field: 'siren',
            label: 'siren',
            type: 'text',
        },
        {
            field: 'libelle',
            label: 'libelle',
            type: 'text',
        },
    ];

    return (
        <>
            <ListHeader
                breadcrumbPath={[
                    { path: 'Fournisseurs', link: '/fournisseurs/liste' },
                    'Liste des fournisseurs',
                ]}
                actions={
                    <HeaderActionButton
                        label="Créer un fournisseur"
                        icon={createPublisherIcon}
                        onClick={() => undefined}
                        disabled
                    />
                }
            />
            <StoreDatatable
                dataSource={publishersDataSource}
                columns={columns}
                fetchData={getFournisseurs}
                filters={filters}
                localStorageKey="FlexyStoreDatatablePublishers"
                onClickRow={handleRowClick}
                onWheelClickRow={handleRowWheelClick}
                observers={[xIdSociete]}
                fetchWithoutFilter
            />
        </>
    );
};

export default PublishersListing;
